import { BitfStrapiModel } from '@common/libs/bitforce/core/models/strapi/bitf-strapi.model';
import { Valuation } from './valuation.model';
import { Vehicle } from './vehicle.model';
import { Image } from './image.model';
import { bitfToTranslate } from '@common/libs/bitforce/utils/bitf-translate.utils';
import { EInvestmentLength } from '@common/enums';

bitfToTranslate('GARAGE.ENUM.GEARBOX_TYPE.MANUAL');
bitfToTranslate('GARAGE.ENUM.GEARBOX_TYPE.AUTOMATIC');

bitfToTranslate('GARAGE.ENUM.TRACTION_TYPE.FRONT');
bitfToTranslate('GARAGE.ENUM.TRACTION_TYPE.REAR');
bitfToTranslate('GARAGE.ENUM.TRACTION_TYPE.ALL_WHEELS');

bitfToTranslate('GARAGE.ENUM.FUEL_TYPE.DIESEL');
bitfToTranslate('GARAGE.ENUM.FUEL_TYPE.GASOLINE');
bitfToTranslate('GARAGE.ENUM.FUEL_TYPE.METHANE');
bitfToTranslate('GARAGE.ENUM.FUEL_TYPE.GPL');

bitfToTranslate('GARAGE.ENUM.STATE.GOOD');
bitfToTranslate('GARAGE.ENUM.STATE.VERY_GOOD');
bitfToTranslate('GARAGE.ENUM.STATE.EXCELLENT');
bitfToTranslate('GARAGE.ENUM.STATE.LIKE_NEW');

export class Garage extends BitfStrapiModel {
  code: string;
  version: string;
  buyPrice: number;
  inStock: boolean;
  isAsi: boolean;
  isAsiGold: boolean;
  guaranteedFutureValue: boolean;
  changeOfOwnershipPrice: number;
  insurancePrice: number;
  carTaxPrice: number;
  maintenancePrice: number;
  garagePrice: number;
  year: number;
  mileage: number;
  engine: string;
  cv: string;
  numberOfGears: number;
  gearboxType: string;
  fuelType: string;
  tractionType: string;
  numberOfSeats: number;
  numberOfDoors: number;
  numberOfPreviousOwners: number;
  numberOfCylinders: number;
  weight: number;
  emissionClass: string;
  interiorState: string;
  exteriorState: string;
  youtubeEmbed: string;
  color: string;
  interiorColor: string;
  vehicleStatusDescription: string;

  // Relations
  vehicle: Vehicle;
  valuations: Valuation[];
  computedValues: {
    valuations: { [investmentLength: string]: Valuation };
    gainsInPercentage: { [investmentLength: string]: number };
    gainsInCash: { [investmentLength: string]: number };
  } = { valuations: {}, gainsInPercentage: {}, gainsInCash: {} };
  gallery: Image[];

  get title() {
    return (
      (this.vehicle?.brand?.name ? this.vehicle.brand.name : '') +
      (this.vehicle?.model ? ' ' + this.vehicle.model : '') +
      (this.vehicle?.modelCode ? ' ' + this.vehicle.modelCode : '') +
      (this.vehicle?.carBody ? ' ' + this.vehicle.carBody : '') +
      (this.version ? ' ' + this.version : '')
    );
  }
  get subTitle() {
    return (this.engine ? this.engine + 'cc ' : '') + (this.year ? this.year + 'my' : '');
  }
  get fullTitle() {
    return this.title + ' ' + this.subTitle;
  }
  get description(): string {
    return this?.vehicle?.vehicleDescription;
  }
  get slug(): string {
    return this.fullTitle.replace(/ /g, '-').toLowerCase();
  }

  get vehicleGallery(): Image[] {
    if (this.gallery) {
      return this.gallery;
    }
    return this?.vehicle?.gallery;
  }

  get firstImage(): Image {
    return this.vehicleGallery[0];
  }

  get youtubeEmbedUrl(): string {
    if (this.youtubeEmbed) {
      const match = this.youtubeEmbed.match(/\<iframe.+src\=(?:\"|\')(.+?)(?:\"|\')(?:.+?)>/);
      if (match && match[1]) {
        return match[1];
      }
    } else if (this.vehicle.youtubeEmbedUrl) {
      return this.vehicle.youtubeEmbedUrl;
    }
  }

  get is30YearsOld() {
    const currentYear = new Date().getFullYear();
    return this.year <= currentYear - 30;
  }

  constructor(data?: any) {
    super({
      data,
      hydrateProperties: {
        vehicle: { model: Vehicle },
        valuations: { model: Valuation },
        gallery: { model: Image },
      },
      serializeProperties: [],
    });
  }

  computeAllGainsInPercentage() {
    for (const investmentLength in EInvestmentLength) {
      if (EInvestmentLength.hasOwnProperty(investmentLength)) {
        this.computedValues.valuations[investmentLength] = this.getValuationForInvestmentDuration(
          investmentLength as EInvestmentLength
        );
        this.computedValues.gainsInCash[investmentLength] = this.getGainInCashForInvestmentDuration(
          investmentLength as EInvestmentLength
        );
        this.computedValues.gainsInPercentage[investmentLength] = this.getGainPercentageForInvestmentDuration(
          investmentLength as EInvestmentLength
        );
      }
    }
  }

  getValuationForInvestmentDuration(investmentDurationLength: EInvestmentLength): Valuation {
    return this.valuations
      .filter(valuation => valuation.sellPrice && valuation.investmentDuration)
      .find(valuation => valuation.investmentDuration.length === investmentDurationLength);
  }

  getGainInCashForInvestmentDuration(investmentDurationLength: EInvestmentLength): number {
    const valuation = this.getValuationForInvestmentDuration(investmentDurationLength);
    if (!valuation) {
      return 0;
    }
    return valuation.sellPrice - this.buyPrice;
  }

  getGainPercentageForInvestmentDuration(investmentDurationLength: EInvestmentLength): number {
    const valuation = this.getValuationForInvestmentDuration(investmentDurationLength);
    if (!valuation) {
      return 0;
    }
    return Number((((valuation.sellPrice - this.buyPrice) / this.buyPrice) * 100).toFixed(1));
  }
}

export const vehicleTechnicalProperties: any = [
  { propertyKey: 'year', translationKey: bitfToTranslate('COMMON.LABEL.YEAR'), keyBefore: true },
  { propertyKey: 'mileage', translationKey: 'KM' },
  {
    propertyKey: 'exteriorState',
    translationKey: bitfToTranslate('COMMON.LABEL.CAR_BODY'),
    enumTranslationKey: 'GARAGE.ENUM.STATE.',
    keyBefore: true,
  },
  {
    propertyKey: 'interiorState',
    translationKey: bitfToTranslate('COMMON.LABEL.INTERIOR'),
    enumTranslationKey: 'GARAGE.ENUM.STATE.',
    keyBefore: true,
  },
  {
    propertyKey: 'engine',
    translationKey: bitfToTranslate('COMMON.LABEL.CC'),
  },
  { propertyKey: 'cv', translationKey: bitfToTranslate('COMMON.LABEL.CV') },
  {
    propertyKey: 'fuelType',
    translationKey: '',
    enumTranslationKey: 'GARAGE.ENUM.FUEL_TYPE.',
  },
  {
    propertyKey: 'numberOfCylinders',
    translationKey: bitfToTranslate('COMMON.LABEL.CYLINDERS'),
  },
  {
    propertyKey: 'numberOfGears',
    translationKey: bitfToTranslate('COMMON.LABEL.GEARS'),
  },
  {
    propertyKey: 'gearboxType',
    translationKey: bitfToTranslate('COMMON.LABEL.GEARBOX'),
    enumTranslationKey: 'GARAGE.ENUM.GEARBOX_TYPE.',
    keyBefore: true,
  },
  {
    propertyKey: 'tractionType',
    translationKey: bitfToTranslate('COMMON.LABEL.TRACTION'),
    enumTranslationKey: 'GARAGE.ENUM.TRACTION_TYPE.',
    keyBefore: true,
  },
  {
    propertyKey: 'color',
    translationKey: bitfToTranslate('COMMON.LABEL.CAR_BODY_COLOR'),
    keyBefore: true,
  },
  {
    propertyKey: 'interiorColor',
    translationKey: bitfToTranslate('COMMON.LABEL.INTERIOR_COLOR'),
    keyBefore: true,
  },
  {
    propertyKey: 'numberOfPreviousOwners',
    translationKey: bitfToTranslate('COMMON.LABEL.PREVIOUS_OWNERS'),
  },

  {
    propertyKey: 'numberOfSeats',
    translationKey: bitfToTranslate('COMMON.LABEL.SEATS'),
  },
  {
    propertyKey: 'numberOfDoors',
    translationKey: bitfToTranslate('COMMON.LABEL.DOORS'),
  },

  {
    propertyKey: 'weight',
    translationKey: bitfToTranslate('COMMON.LABEL.KG'),
  },
  {
    propertyKey: 'emissionClass',
    translationKey: bitfToTranslate('COMMON.LABEL.EMISSIONS'),
    keyBefore: true,
  },
];
